import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OrganizationInsurance } from '@aid/administration/types/classes/organization-insurance.class';
import { BehaviorSubject, Observable } from 'rxjs';
import { InsuranceType } from '@aid/administration/types/classes';
import { Params } from '@angular/router';
import { UnredirectedRoutesService } from '@aid/core/services';

/** Partial write payload; the product partner is sent by id, unlike the read model. */
export interface OrganizationInsuranceUpdate {
  productPartner?: number | null;
  number?: number;
}

@Injectable({
  providedIn: 'root'
})
export class OrganizationInsuranceService {
  private provider$ = new BehaviorSubject<number | null>(null);

  get url(): string {
    return `providers/${this.provider$.value}/insurance-types`;
  }

  constructor(
    private http: HttpClient,
    private unredirectedRoutesService: UnredirectedRoutesService
  ) {
    this.unredirectedRoutesService.subscribeRoute('insurance-types');
  }

  setProvider(provider: number | null) {
    this.provider$.next(provider);
  }

  getProvider$() {
    return this.provider$.asObservable();
  }

  getProviderValue() {
    return this.provider$.value;
  }

  // ProviderGuard sets the provider before the insurance-type and
  // not-configured routes render.
  requireProvider(): number {
    const provider = this.provider$.value;
    if (provider === null) {
      throw new Error('No provider is selected.');
    }
    return provider;
  }

  setProductPartner(productPartnerId: number) {
    const body = {
      productPartner: productPartnerId
    };
    return this.http.patch<OrganizationInsurance[]>(
      `${this.url}/product-partners`,
      body
    );
  }

  list(): Observable<OrganizationInsurance[]> {
    return this.http.get<OrganizationInsurance[]>(`${this.url}`);
  }

  get(insuranceTypeId: number): Observable<OrganizationInsurance> {
    return this.http.get<OrganizationInsurance>(
      `${this.url}/${insuranceTypeId}`
    );
  }

  getDetails(
    insuranceTypeId: number,
    providerId: number
  ): Observable<OrganizationInsurance> {
    this.setProvider(providerId);
    return this.http.get<OrganizationInsurance>(
      `${this.url}/${insuranceTypeId}/details`
    );
  }

  add(insuranceTypeId: number): Observable<OrganizationInsurance> {
    const body = {
      insuranceType: insuranceTypeId
    };
    return this.http.post<OrganizationInsurance>(this.url, body);
  }

  delete(insuranceTypeId: number): Observable<void> {
    return this.http.delete<void>(`${this.url}/${insuranceTypeId}`);
  }

  addAll(): Observable<OrganizationInsurance[]> {
    return this.http.post<OrganizationInsurance[]>(`${this.url}/add`, {});
  }

  deleteAll(): Observable<void> {
    return this.http.delete<void>(`${this.url}/remove`, {});
  }

  edit(insuranceTypeId: number, body: OrganizationInsuranceUpdate) {
    return this.http.patch<OrganizationInsurance>(
      `${this.url}/${insuranceTypeId}`,
      body
    );
  }

  providerInsuranceTypes(providerId: number): Observable<InsuranceType[]> {
    const url = `insurance-types`;
    const params = { provider: providerId } as Params;
    return this.http.get<InsuranceType[]>(url, { params });
  }
}
